import sortBy from 'lodash/sortBy';

/**
 * Whether a practitioner should be treated as inactive.
 *
 * In-app deactivation flips `User.active`, NOT `Practitioner.active` (only the
 * CSV importer, api/src/Command/ImportCSVContactFileCommand.php, sets
 * `Practitioner.active`). So a practitioner is inactive when the linked
 * `User.active === false` (resolved via `userActiveById`) OR — as a defensive
 * fallback for CSV-imported data — its own `Practitioner.active === false`.
 * A practitioner with no matching User and no explicit `active === false`
 * defaults to active. See #4883 / #4168.
 *
 * @param {Object} practitioner - a Practitioner resource (or object carrying `id`/`active`)
 * @param {Map<string, boolean>} [userActiveById] - practitioner id -> linked User.active
 * @returns {boolean}
 */
export function isInactivePractitioner(practitioner, userActiveById) {
    if (!practitioner) return false;
    if (userActiveById?.has(practitioner.id) && userActiveById.get(practitioner.id) === false) {
        return true;
    }
    return practitioner.active === false;
}

/**
 * Build a lookup of practitioner id -> linked `User.active` from a mixed
 * User/Practitioner array (the shape returned by a `User?_include=practitioner`
 * query). Undefined/absent `User.active` is treated as active (`true`).
 *
 * @param {Array} resources - mixed array of User and Practitioner resources
 * @returns {Map<string, boolean>}
 */
export function buildUserActiveMap(resources) {
    const userActiveById = new Map();
    if (!Array.isArray(resources)) return userActiveById;
    resources.forEach((res) => {
        if (res?.resourceType === 'User' && res.practitioner?.id) {
            userActiveById.set(res.practitioner.id, res.active !== false);
        }
    });
    return userActiveById;
}

/**
 * Split a mixed User/Practitioner resource array into active and inactive
 * practitioner groups for a picker's Active / Inactive sections, each sorted
 * alphabetically by name. Inactivity is sourced from the linked `User.active`
 * (see isInactivePractitioner) so the Inactive section reflects in-app
 * deactivation — filtering on `Practitioner.active` alone leaves it perpetually
 * empty. (#4883)
 *
 * Returns the raw Practitioner resources; callers map them into their display
 * shape within each group so grouping/status stays centralized here.
 *
 * @param {Object} params
 * @param {Array} params.resources - mixed array of User and Practitioner resources
 * @param {Function} [params.nameFn] - practitioner -> display name for sorting (default: `fullName`)
 * @returns {{ active: Array, inactive: Array }}
 */
export function groupPractitionersByStatus({ resources, nameFn } = {}) {
    if (!resources || !resources.length) {
        return { active: [], inactive: [] };
    }

    const userActiveById = buildUserActiveMap(resources);
    const getName = nameFn || ((p) => p?.fullName);
    const practitioners = resources.filter((res) => res?.resourceType === 'Practitioner');

    const active = [];
    const inactive = [];
    practitioners.forEach((practitioner) => {
        (isInactivePractitioner(practitioner, userActiveById) ? inactive : active).push(practitioner);
    });

    const byName = (p) => (getName(p) ?? '').toLowerCase();
    return {
        active: sortBy(active, [byName]),
        inactive: sortBy(inactive, [byName]),
    };
}

/**
 * Flatten `{ active, inactive }` groups into a single `v-select`/`v-autocomplete`
 * items array with an "Inactive" section, following the sentinel-row convention
 * already used by these pickers: `{ header }` rows render as `<v-list-subheader>`
 * and `{ divider: true }` rows as `<v-divider>` in the `#item` slot. Active rows
 * come first (no header — the field label already says "Provider"), then a
 * divider + "Inactive" subheader, then the inactive rows. When there are no
 * inactive practitioners the section is omitted entirely.
 *
 * @param {{ active: Array, inactive: Array }} groups
 * @returns {Array} flat items array (option objects interleaved with `{ header }` / `{ divider }`)
 */
export function toSectionedProviderItems({ active = [], inactive = [] } = {}) {
    if (!inactive.length) return [...active];
    return [...active, { divider: true }, { header: 'Inactive' }, ...inactive];
}
